import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { invoicesService, api } from '../../api/apiClient';
import { useToast } from '../../context/ToastContext';
import { Card, CardHeader, CardBody } from '../../components/UI/Card';
import { Button } from '../../components/UI/Button';
import { Input, Select } from '../../components/UI/Input';
import { Modal } from '../../components/UI/Modal';
import { ConfirmDialog } from '../../components/UI/ConfirmDialog';
import { Pagination } from '../../components/UI/Pagination';
import { Spinner } from '../../components/UI/Spinner';
import { EmptyState } from '../../components/UI/EmptyState';
import {
  Receipt,
  Plus,
  Search,
  Eye,
  Edit2,
  Trash2,
  Download,
  ExternalLink,
  CreditCard,
  Clock,
  CheckCircle,
  XCircle,
  ChevronDown,
  DollarSign,
  AlertCircle,
} from 'lucide-react';

export const InvoiceList = () => {
  const [invoices, setInvoices] = useState([]);
  const [pagination, setPagination] = useState({});
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  // Delete invoice
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Status Change dropdown & handling
  const [activeStatusDropdown, setActiveStatusDropdown] = useState(null);
  const [statusLoadingId, setStatusLoadingId] = useState(null);
  const [pendingWarningModal, setPendingWarningModal] = useState(null);

  // Payment Modal state
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [selectedInvoiceForPay, setSelectedInvoiceForPay] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('Cash');
  const [payDescription, setPayDescription] = useState('');
  const [paySubmitting, setPaySubmitting] = useState(false);

  // Payments List Modal state
  const [paymentsModalOpen, setPaymentsModalOpen] = useState(false);
  const [selectedInvoiceForPayments, setSelectedInvoiceForPayments] = useState(null);
  const [invoicePaymentsList, setInvoicePaymentsList] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const [deletePaymentConfirmOpen, setDeletePaymentConfirmOpen] = useState(false);

  const { success, error: toastError } = useToast();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveStatusDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await invoicesService.list({ search, page, limit: 15 });
      if (res && res.data) {
        if (res.data.data) {
          setInvoices(res.data.data);
          setPagination({
            currentPage: res.data.current_page,
            lastPage: res.data.last_page,
            total: res.data.total,
            from: res.data.from,
            to: res.data.to,
          });
        } else {
          setInvoices(Array.isArray(res.data) ? res.data : []);
        }
      }
    } catch (err) {
      toastError(err.message || 'Error al cargar facturas.');
    } finally {
      setLoading(false);
    }
  }, [search, page, toastError]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchInvoices();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchInvoices]);

  // Helper for invoice calculation
  const getInvoiceAmounts = (inv) => {
    const total = Number(inv.total || 0);
    const paid = (inv.payments || []).reduce((acc, p) => acc + Number(p.amount || 0), 0);
    const pending = Math.max(0, total - paid);
    return { total, paid, pending };
  };

  // Status Change Handler
  const handleStatusChange = async (inv, newStatus) => {
    setActiveStatusDropdown(null);
    if (Number(inv.status) === Number(newStatus)) return;

    const { pending } = getInvoiceAmounts(inv);

    // If changing to Paid (1) but balance is pending
    if (Number(newStatus) === 1 && pending > 0.01) {
      setPendingWarningModal({
        invoice: inv,
        pendingAmount: pending.toFixed(2),
        symbol: inv.currency?.symbol || '$',
      });
      return;
    }

    setStatusLoadingId(inv.id);
    try {
      await invoicesService.changeStatus(inv.id, newStatus);
      const statusLabels = { 0: 'Abierta', 1: 'Pagada', 2: 'Cerrada' };
      success(`Factura #${inv.number || inv.id} marcada como ${statusLabels[newStatus]}.`);
      fetchInvoices();
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al actualizar estado.');
    } finally {
      setStatusLoadingId(null);
    }
  };

  // Open Payment Modal
  const openPayModal = (inv) => {
    const { pending } = getInvoiceAmounts(inv);
    setSelectedInvoiceForPay(inv);
    setPayAmount(pending > 0 ? pending.toFixed(2) : '');
    setPayMethod('Cash');
    setPayDescription('');
    setPayModalOpen(true);
  };

  // Submit Payment
  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!selectedInvoiceForPay) return;

    setPaySubmitting(true);
    try {
      await invoicesService.pay(selectedInvoiceForPay.id, {
        amount: payAmount,
        method: payMethod,
        description: payDescription,
      });
      success('Pago registrado correctamente.');
      setPayModalOpen(false);
      setSelectedInvoiceForPay(null);
      fetchInvoices();

      // If payments modal was also open, refresh it
      if (paymentsModalOpen && selectedInvoiceForPayments?.id === selectedInvoiceForPay.id) {
        loadInvoicePayments(selectedInvoiceForPay.id);
      }
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al registrar el pago.');
    } finally {
      setPaySubmitting(false);
    }
  };

  // Load and open Payments List modal
  const loadInvoicePayments = async (invoiceId) => {
    setPaymentsLoading(true);
    try {
      const res = await invoicesService.get(invoiceId);
      if (res && res.data) {
        setSelectedInvoiceForPayments(res.data.invoice);
        setInvoicePaymentsList(res.data.payments || []);
      }
    } catch (err) {
      toastError('No se pudo cargar el historial de pagos.');
    } finally {
      setPaymentsLoading(false);
    }
  };

  const openPaymentsListModal = (inv) => {
    setSelectedInvoiceForPayments(inv);
    setInvoicePaymentsList(inv.payments || []);
    setPaymentsModalOpen(true);
    loadInvoicePayments(inv.id);
  };

  // Delete a Payment
  const handleDeletePayment = async () => {
    if (!paymentToDelete || !selectedInvoiceForPayments) return;
    try {
      await invoicesService.deletePayment(selectedInvoiceForPayments.id, paymentToDelete.id);
      success('Pago eliminado correctamente.');
      setDeletePaymentConfirmOpen(false);
      setPaymentToDelete(null);
      // Reload payments and invoice list
      loadInvoicePayments(selectedInvoiceForPayments.id);
      fetchInvoices();
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al eliminar pago.');
    }
  };

  // Delete invoice
  const handleDeleteInvoice = async () => {
    if (!invoiceToDelete) return;
    setSubmitting(true);
    try {
      await invoicesService.delete(invoiceToDelete.id);
      success('Factura eliminada correctamente.');
      setDeleteConfirmOpen(false);
      setInvoiceToDelete(null);
      fetchInvoices();
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al eliminar factura.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadPdf = async (inv) => {
    try {
      await invoicesService.downloadPdf(inv.id, inv.number);
      success('PDF de factura descargado.');
    } catch (err) {
      toastError('No se pudo descargar el archivo PDF.');
    }
  };

  const handleOpenPdf = (inv) => {
    window.open(api.getPdfUrl('invoices', inv.id), '_blank');
  };

  const getStatusBadgeConfig = (status) => {
    switch (Number(status)) {
      case 1:
        return {
          label: 'Pagada',
          variant: 'success',
          icon: CheckCircle,
          className: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800',
        };
      case 2:
        return {
          label: 'Cerrada',
          variant: 'default',
          icon: XCircle,
          className: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
        };
      default:
        return {
          label: 'Abierta',
          variant: 'warning',
          icon: Clock,
          className: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-800',
        };
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight flex items-center gap-2">
            <Receipt className="w-6 h-6 text-[#08707A] dark:text-teal-400" />
            Facturación & Cobros
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
            Control de cuentas por cobrar, estados de pago y emisión de facturas
          </p>
        </div>

        <Link to="/invoices/create">
          <Button icon={Plus} className="bg-[#08707A] hover:bg-[#065B63]">
            Nueva Factura
          </Button>
        </Link>
      </div>

      {/* List Card */}
      <Card>
        <CardHeader>
          <div className="w-full sm:w-80">
            <Input
              placeholder="Buscar por número o cliente..."
              icon={Search}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="py-1.5"
            />
          </div>
        </CardHeader>

        <CardBody className="p-0">
          {loading ? (
            <Spinner label="Cargando facturas..." className="py-12" />
          ) : invoices.length === 0 ? (
            <EmptyState
              icon={Receipt}
              title="No hay facturas emitidas"
              description={search ? "No se encontraron facturas para los términos de búsqueda." : "Genera tu primera factura para comenzar a registrar pagos y cobros."}
              actionLabel="Crear Factura"
              onAction={() => navigate('/invoices/create')}
              actionIcon={Plus}
            />
          ) : (
            <div className="overflow-x-auto min-h-[300px]">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                  <tr>
                    <th className="py-3 px-4">Número</th>
                    <th className="py-3 px-4">Cliente</th>
                    <th className="py-3 px-4">Fecha Emisión</th>
                    <th className="py-3 px-4">Vencimiento</th>
                    <th className="py-3 px-4">Total & Cobros</th>
                    <th className="py-3 px-4 text-center">Estado</th>
                    <th className="py-3 px-4 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {invoices.map((inv) => {
                    const { total, paid, pending } = getInvoiceAmounts(inv);
                    const symbol = inv.currency?.symbol || '$';
                    const isStatusLoading = statusLoadingId === inv.id;
                    const statusConfig = getStatusBadgeConfig(inv.status);
                    const StatusIcon = statusConfig.icon;
                    const isDropdownOpen = activeStatusDropdown === inv.id;

                    return (
                      <tr
                        key={inv.id}
                        className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors"
                      >
                        {/* Number */}
                        <td className="py-3.5 px-4 font-mono font-bold text-slate-900 dark:text-slate-100">
                          #{inv.number || String(inv.id).padStart(6, '0')}
                        </td>

                        {/* Customer */}
                        <td className="py-3.5 px-4">
                          <div className="font-bold text-slate-900 dark:text-slate-100">
                            {inv.customer?.full_name || 'Cliente sin asignar'}
                          </div>
                          {inv.customer?.email && (
                            <div className="text-[11px] text-slate-400">{inv.customer.email}</div>
                          )}
                        </td>

                        {/* Issue Date */}
                        <td className="py-3.5 px-4 text-slate-600 dark:text-slate-300">
                          {inv.invo_date || '—'}
                        </td>

                        {/* Due Date */}
                        <td className="py-3.5 px-4 text-slate-600 dark:text-slate-300">
                          {inv.due_date || '—'}
                        </td>

                        {/* Totals & Payments breakdown */}
                        <td className="py-3.5 px-4">
                          <div className="font-black text-sm text-slate-900 dark:text-slate-100">
                            {symbol} {total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </div>
                          <div className="flex items-center gap-1.5 mt-0.5 text-[11px]">
                            {paid >= total && total > 0 ? (
                              <span className="text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-0.5">
                                <CheckCircle className="w-3 h-3" /> Cobrado total
                              </span>
                            ) : paid > 0 ? (
                              <>
                                <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                                  Cobrado: {symbol}{paid.toFixed(2)}
                                </span>
                                <span className="text-slate-300 dark:text-slate-600">&middot;</span>
                                <span className="text-amber-600 dark:text-amber-400 font-semibold">
                                  Resta: {symbol}{pending.toFixed(2)}
                                </span>
                              </>
                            ) : (
                              <span className="text-slate-400 dark:text-slate-500 font-medium">
                                Pendiente: {symbol}{pending.toFixed(2)}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Status Interactive Dropdown */}
                        <td className="py-3.5 px-4 text-center">
                          <div className="relative inline-block text-left" ref={isDropdownOpen ? dropdownRef : null}>
                            <button
                              type="button"
                              onClick={() => setActiveStatusDropdown(isDropdownOpen ? null : inv.id)}
                              disabled={isStatusLoading}
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-lg border transition-all shadow-sm hover:opacity-90 ${statusConfig.className} ${isStatusLoading ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                              title="Click para cambiar estado"
                            >
                              <StatusIcon className="w-3.5 h-3.5" />
                              <span>{statusConfig.label}</span>
                              <ChevronDown className="w-3 h-3 opacity-60 ml-0.5" />
                            </button>

                            {/* Dropdown Menu */}
                            {isDropdownOpen && (
                              <div className="absolute left-1/2 -translate-x-1/2 mt-1 w-36 bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-800 py-1 z-30 animate-in fade-in zoom-in-95 duration-100">
                                <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100 dark:border-slate-800">
                                  Cambiar Estado
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleStatusChange(inv, 0)}
                                  className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-amber-50 dark:hover:bg-amber-950/40 transition-colors ${Number(inv.status) === 0 ? 'text-amber-600 font-bold bg-amber-50/50 dark:bg-amber-950/20' : 'text-slate-700 dark:text-slate-300'}`}
                                >
                                  <Clock className="w-3.5 h-3.5 text-amber-500" />
                                  Abierta
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleStatusChange(inv, 1)}
                                  className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 transition-colors ${Number(inv.status) === 1 ? 'text-emerald-600 font-bold bg-emerald-50/50 dark:bg-emerald-950/20' : 'text-slate-700 dark:text-slate-300'}`}
                                >
                                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                                  Pagada
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleStatusChange(inv, 2)}
                                  className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors ${Number(inv.status) === 2 ? 'text-slate-900 dark:text-white font-bold bg-slate-100 dark:bg-slate-800' : 'text-slate-700 dark:text-slate-300'}`}
                                >
                                  <XCircle className="w-3.5 h-3.5 text-slate-500" />
                                  Cerrada
                                </button>
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Actions */}
                        <td className="py-3.5 px-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {/* Record Payment Button */}
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={CreditCard}
                              onClick={() => openPayModal(inv)}
                              title="Registrar Abono / Pago"
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/40"
                            />

                            {/* View Payments List Button */}
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={DollarSign}
                              onClick={() => openPaymentsListModal(inv)}
                              title="Ver Historial de Pagos"
                              className="text-teal-600 hover:text-teal-700 hover:bg-teal-50 dark:hover:bg-teal-950/40"
                            />

                            {/* View Detail */}
                            <Link to={`/invoices/${inv.id}`}>
                              <Button variant="ghost" size="sm" icon={Eye} title="Ver Detalle Completo" />
                            </Link>

                            {/* View PDF */}
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={ExternalLink}
                              onClick={() => handleOpenPdf(inv)}
                              title="Ver PDF en navegador"
                            />

                            {/* Download PDF */}
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={Download}
                              onClick={() => handleDownloadPdf(inv)}
                              title="Descargar PDF"
                            />

                            {/* Edit */}
                            <Link to={`/invoices/edit/${inv.id}`}>
                              <Button variant="ghost" size="sm" icon={Edit2} title="Editar Factura" />
                            </Link>

                            {/* Delete */}
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={Trash2}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40"
                              onClick={() => {
                                setInvoiceToDelete(inv);
                                setDeleteConfirmOpen(true);
                              }}
                              title="Eliminar"
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <Pagination
            currentPage={pagination.currentPage}
            lastPage={pagination.lastPage}
            total={pagination.total}
            from={pagination.from}
            to={pagination.to}
            onPageChange={setPage}
          />
        </CardBody>
      </Card>

      {/* Delete Invoice Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDeleteInvoice}
        title="¿Eliminar factura?"
        message={`¿Estás seguro de que deseas eliminar la factura #${invoiceToDelete?.number}? Se eliminarán los registros de pago e historial asociados.`}
        confirmText="Eliminar Factura"
        isLoading={submitting}
      />

      {/* Record Payment Modal */}
      <Modal
        isOpen={payModalOpen}
        onClose={() => !paySubmitting && setPayModalOpen(false)}
        title={`Registrar Pago · Factura #${selectedInvoiceForPay?.number || ''}`}
        subtitle={
          selectedInvoiceForPay
            ? `Cliente: ${selectedInvoiceForPay.customer?.full_name || 'Sin cliente'} · Saldo pendiente: ${selectedInvoiceForPay.currency?.symbol || '$'} ${getInvoiceAmounts(selectedInvoiceForPay).pending.toFixed(2)}`
            : ''
        }
      >
        <form onSubmit={handleRecordPayment} className="p-6 space-y-4">
          <Input
            label="Monto a Cobrar"
            type="number"
            step="0.01"
            min="0.01"
            placeholder="0.00"
            value={payAmount}
            onChange={(e) => setPayAmount(e.target.value)}
            required
            autoFocus
          />

          <Select
            label="Método de Pago"
            value={payMethod}
            onChange={(e) => setPayMethod(e.target.value)}
          >
            <option value="Cash">Efectivo</option>
            <option value="Bank Transfer">Transferencia Bancaria</option>
            <option value="Credit Card">Tarjeta de Crédito / Débito</option>
            <option value="PayPal">PayPal</option>
            <option value="Check">Cheque</option>
            <option value="Other">Otro</option>
          </Select>

          <Input
            label="Nota / Referencia de Pago (Opcional)"
            placeholder="Ej: Transferencia Banco XYZ Ref #12345"
            value={payDescription}
            onChange={(e) => setPayDescription(e.target.value)}
          />

          <div className="pt-4 flex items-center justify-end gap-3 border-t border-slate-100 dark:border-slate-800">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setPayModalOpen(false)}
              disabled={paySubmitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={paySubmitting}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Guardar Pago
            </Button>
          </div>
        </form>
      </Modal>

      {/* Payments List Modal */}
      <Modal
        isOpen={paymentsModalOpen}
        onClose={() => setPaymentsModalOpen(false)}
        title={`Pagos de Factura #${selectedInvoiceForPayments?.number || ''}`}
        subtitle={`Cliente: ${selectedInvoiceForPayments?.customer?.full_name || 'Sin cliente'}`}
      >
        <div className="p-6 space-y-5">
          {/* Summary Box */}
          {selectedInvoiceForPayments && (() => {
            const { total, paid, pending } = getInvoiceAmounts(selectedInvoiceForPayments);
            const symbol = selectedInvoiceForPayments.currency?.symbol || '$';
            const pct = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 0;

            return (
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-100 dark:border-slate-800 space-y-3">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <span className="text-[10px] uppercase font-bold text-slate-400">Total</span>
                    <p className="text-sm font-black text-slate-900 dark:text-white">
                      {symbol} {total.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <span className="text-[10px] uppercase font-bold text-slate-400">Cobrado</span>
                    <p className="text-sm font-black text-emerald-600 dark:text-emerald-400">
                      {symbol} {paid.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <span className="text-[10px] uppercase font-bold text-slate-400">Pendiente</span>
                    <p className={`text-sm font-black ${pending > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600'}`}>
                      {symbol} {pending.toFixed(2)}
                    </p>
                  </div>
                </div>

                {/* Progress bar */}
                <div>
                  <div className="flex justify-between text-[11px] font-semibold text-slate-500 mb-1">
                    <span>Progreso de cobro</span>
                    <span>{pct}%</span>
                  </div>
                  <div className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${pct >= 100 ? 'bg-emerald-500' : 'bg-[#08707A]'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Payments Listing */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">
                Abonos Registrados ({invoicePaymentsList.length})
              </h4>
              <Button
                size="sm"
                variant="secondary"
                icon={Plus}
                onClick={() => {
                  if (selectedInvoiceForPayments) {
                    openPayModal(selectedInvoiceForPayments);
                  }
                }}
                className="text-xs py-1"
              >
                Nuevo Pago
              </Button>
            </div>

            {paymentsLoading ? (
              <Spinner label="Cargando pagos..." className="py-6" />
            ) : invoicePaymentsList.length === 0 ? (
              <div className="p-8 text-center border border-dashed border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-400">
                No hay pagos registrados para esta factura.
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-800 border border-slate-100 dark:border-slate-800 rounded-xl overflow-hidden max-h-64 overflow-y-auto">
                {invoicePaymentsList.map((p) => {
                  const symbol = selectedInvoiceForPayments?.currency?.symbol || '$';
                  return (
                    <div key={p.id} className="p-3.5 flex items-center justify-between gap-4 text-xs hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-slate-900 dark:text-slate-100 text-sm">
                            {symbol} {Number(p.amount).toFixed(2)}
                          </span>
                          <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-teal-50 text-[#08707A] dark:bg-teal-950/60 dark:text-teal-300 border border-teal-200 dark:border-teal-800">
                            {p.method || 'Efectivo'}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-400 mt-0.5">
                          {new Date(p.created_at).toLocaleString()} {p.description ? `· ${p.description}` : ''}
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          setPaymentToDelete(p);
                          setDeletePaymentConfirmOpen(true);
                        }}
                        className="text-slate-400 hover:text-red-600 transition-colors p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/40"
                        title="Eliminar este abono"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="pt-2 flex justify-end">
            <Button variant="secondary" onClick={() => setPaymentsModalOpen(false)}>
              Cerrar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Pending Balance Warning / Quick Pay Prompt */}
      <Modal
        isOpen={Boolean(pendingWarningModal)}
        onClose={() => setPendingWarningModal(null)}
        title="Saldo Pendiente Requerido"
        subtitle={`Factura #${pendingWarningModal?.invoice?.number}`}
      >
        <div className="p-6 space-y-4">
          <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="text-xs space-y-1">
              <p className="font-bold text-amber-900 dark:text-amber-200">
                La factura no puede marcarse como PAGADA directamente.
              </p>
              <p className="text-amber-700 dark:text-amber-300">
                Existe un saldo pendiente de{' '}
                <strong>
                  {pendingWarningModal?.symbol} {pendingWarningModal?.pendingAmount}
                </strong>
                . El sistema requiere que los pagos registrados cubran el total de la factura antes de pasar a estado Pagada.
              </p>
            </div>
          </div>

          <p className="text-xs text-slate-600 dark:text-slate-300">
            ¿Deseas registrar un pago por el monto restante de{' '}
            <strong className="text-slate-900 dark:text-white">
              {pendingWarningModal?.symbol} {pendingWarningModal?.pendingAmount}
            </strong>{' '}
            para marcarla automáticamente como Pagada?
          </p>

          <div className="pt-4 flex items-center justify-end gap-3 border-t border-slate-100 dark:border-slate-800">
            <Button variant="secondary" onClick={() => setPendingWarningModal(null)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              className="bg-emerald-600 hover:bg-emerald-700"
              icon={CreditCard}
              onClick={() => {
                const inv = pendingWarningModal.invoice;
                setPendingWarningModal(null);
                openPayModal(inv);
              }}
            >
              Registrar Saldo Restante
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Payment Confirm Dialog */}
      <ConfirmDialog
        isOpen={deletePaymentConfirmOpen}
        onClose={() => setDeletePaymentConfirmOpen(false)}
        onConfirm={handleDeletePayment}
        title="¿Eliminar registro de pago?"
        message={`Se eliminará el pago de ${selectedInvoiceForPayments?.currency?.symbol || '$'} ${Number(paymentToDelete?.amount || 0).toFixed(2)}. El saldo pendiente de la factura se recalculará automáticamente.`}
        confirmText="Eliminar Pago"
      />
    </div>
  );
};
